// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Stable color palette so the same entity is the same color across views.

import type { Medium } from './types';

export const MEDIUM_COLORS: Record<Medium, string> = {
  air: '#0f766e',     // teal — air
  water: '#0284c7',   // blue — water
  land: '#a16207'     // earth brown — land
};

// ANZSIC division → color. Stable across views.
export const DIVISION_COLORS: Record<string, string> = {
  A: '#65a30d',       // Agriculture, Forestry and Fishing — green
  B: '#a16207',       // Mining — earth brown
  C: '#dc2626',       // Manufacturing — red
  D: '#f59e0b',       // Electricity, Gas, Water and Waste Services — amber
  E: '#6b7280',       // Construction — grey
  F: '#7c3aed',       // Wholesale Trade — purple
  G: '#ec4899',       // Retail Trade — pink
  H: '#06b6d4',       // Accommodation and Food Services — cyan
  I: '#0ea5e9',       // Transport, Postal and Warehousing — sky
  J: '#8b5cf6',       // Information Media and Telecommunications — violet
  K: '#10b981',       // Financial and Insurance Services — emerald
  L: '#84cc16',       // Rental, Hiring and Real Estate — lime
  M: '#f97316',       // Professional, Scientific and Technical Services — orange
  N: '#14b8a6',       // Administrative and Support Services — teal
  O: '#475569',       // Public Administration and Safety — slate
  P: '#0891b2',       // Education and Training
  Q: '#be185d',       // Health Care and Social Assistance
  R: '#9333ea',       // Arts and Recreation
  S: '#525252'        // Other Services
};

export function divisionColor(code: string | null | undefined): string {
  if (!code) return '#94a3b8';
  return DIVISION_COLORS[code] ?? '#94a3b8';
}

// Substance category → color
export const SUBSTANCE_CATEGORY_COLORS: Record<string, string> = {
  'Heavy metals': '#7c2d12',
  'Organics & VOCs': '#7c3aed',
  'Particulates': '#475569',
  'Criteria air pollutants': '#dc2626',
  'Acid gases & inorganics': '#0891b2',
  'Persistent organic pollutants': '#831843',
  'Other inorganics': '#0f766e',
  'Other': '#94a3b8'
};

export function substanceCategoryColor(category: string): string {
  return SUBSTANCE_CATEGORY_COLORS[category] ?? '#94a3b8';
}

export const STATE_COLORS: Record<string, string> = {
  NSW: '#0ea5e9',
  VIC: '#7c3aed',
  QLD: '#dc2626',
  WA: '#f59e0b',
  SA: '#a16207',
  TAS: '#0f766e',
  ACT: '#475569',
  NT: '#ea580c'
};

export function stateColor(state: string | null | undefined): string {
  if (!state) return '#94a3b8';
  return STATE_COLORS[state] ?? '#94a3b8';
}

// Heatmap intensity (0..1 → color). Light teal → dark teal.
export function heatColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  // Mix #e6fffa → #115e59
  const start = [230, 255, 250];
  const end = [17, 94, 89];
  const mix = start.map((s, i) => Math.round(s + (end[i] - s) * clamped));
  return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
}
